import { createContext, useContext, useState, useEffect } from 'react';
import { authenticateUser, updateLastLogin, ROLES, PERMISSIONS } from '../services/firebaseAuth';
import { logLogin, logLogout } from '../utils/loggingWrapper';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check for existing user session
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const userData = JSON.parse(storedUser);
                setUser(userData);
            } catch (error) {
                console.error('Error parsing stored user data:', error);
                localStorage.removeItem('user');
            }
        }
        setLoading(false);
    }, []);

    const login = async (username, password) => {
        const result = await authenticateUser(username, password);

        if (result.success) {
            setUser(result.user);
            localStorage.setItem('user', JSON.stringify(result.user));
            localStorage.setItem('username', username); // Keep for backward compatibility

            // Update last login in Firebase
            await updateLastLogin(result.user.id, result.user.role);

            // Log the login activity
            await logLogin(result.user);

            return { success: true, user: result.user };
        } else {
            return { success: false, error: result.error };
        }
    };

    const logout = async () => {
        // Log the logout activity before clearing user data
        if (user) {
            await logLogout(user);
        }

        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('username');
    };

    const hasPermission = (permission) => {
        if (!user) return false;
        return user.permissions.includes(permission);
    };

    const hasRole = (role) => {
        if (!user) return false;
        return user.role === role;
    };

    const isAuthenticated = () => {
        return user !== null;
    };

    const value = {
        user,
        loading,
        login,
        logout,
        hasPermission,
        hasRole,
        isAuthenticated,
        ROLES,
        PERMISSIONS
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
